import { useState, type CSSProperties, type FormEvent } from 'react';
import { api, ApiError, setToken } from '../lib/api';
import { Orb } from '../components/Orb';

/** Full-screen founder sign-in: username + password. Default password = username. */
export default function Login({ onAuthed }: { onAuthed: () => void }) {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  // Set by api.ts when a live session expired and bounced us here — explain why,
  // so a founder doesn't think the app logged them out for no reason.
  const [expired] = useState(() => {
    try {
      if (sessionStorage.getItem('hq_session_expired')) {
        sessionStorage.removeItem('hq_session_expired');
        return true;
      }
    } catch {
      /* ignore */
    }
    return false;
  });

  async function submit(e: FormEvent) {
    e.preventDefault();
    if (!username || !password) return;
    setBusy(true);
    setError('');
    try {
      const r = await api.post<{ ok: boolean; token: string }>('/auth/login', { username: username.trim(), password });
      setToken(r.token);
      onAuthed();
    } catch (err) {
      setError(
        err instanceof ApiError && err.status === 401
          ? 'Wrong username or password.'
          : 'Something went wrong. Try again.',
      );
    } finally {
      setBusy(false);
    }
  }

  return (
    <div style={wrap}>
      <form className="card" onSubmit={submit} style={{ width: 380, maxWidth: '100%', padding: 30 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12, justifyContent: 'center', marginBottom: 22 }}>
          <Orb agent="emma" size="xs" />
          <span style={{ fontWeight: 250, fontSize: 20 }}>PuppyPad HQ</span>
        </div>

        <label className="t-label" htmlFor="u">Username</label>
        <input
          id="u"
          value={username}
          onChange={(e) => setUsername(e.target.value)}
          autoFocus
          autoCapitalize="none"
          autoComplete="username"
          placeholder="zarg"
          style={input}
        />

        <label className="t-label" htmlFor="p" style={{ marginTop: 14, display: 'block' }}>Password</label>
        <input
          id="p"
          type="password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          autoComplete="current-password"
          placeholder="••••••"
          style={input}
        />

        {expired && !error && (
          <p style={{ color: 'var(--amber)', fontSize: 13, marginTop: 12 }}>
            Your session timed out — please sign in again.
          </p>
        )}
        {error && <p style={{ color: 'var(--coral)', fontSize: 13, marginTop: 12 }}>{error}</p>}

        <button className="btn btn-primary" disabled={busy} style={{ width: '100%', justifyContent: 'center', marginTop: 18 }}>
          {busy ? 'Signing in…' : 'Sign in'}
        </button>
        <p className="small" style={{ textAlign: 'center', marginTop: 14 }}>
          Founders only. First time? Your password is your username — change it in Settings.
        </p>
      </form>
    </div>
  );
}

const wrap: CSSProperties = {
  position: 'relative',
  zIndex: 1,
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
};
const input: CSSProperties = {
  width: '100%',
  marginTop: 6,
  padding: '12px 14px',
  borderRadius: 'var(--r-ctl)',
  border: '1px solid var(--line-2)',
  background: 'var(--panel-2)',
  color: 'var(--text)',
  fontFamily: 'var(--font)',
  fontSize: 15,
};
